import { useRef, useState } from "react"
import { useFileUpload } from "@/hooks/useFileUpload"
import type { UploadedFileDto } from "@/api/types/uploadedFile.types"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Loader2, Upload, X, Image as ImageIcon } from "lucide-react"
import { formatFileSize } from "@/lib/fileValidation"
import { cn } from "@/lib/utils"

interface FileUploadProps {
  // File configuration
  value?: string // Existing file ID
  onChange?: (fileId: string | null) => void // Callback with file ID

  // Validation
  allowedTypes?: string[] // Default: image/* MIME types
  maxSize?: number // Default: 5MB

  // UI customization
  label?: string
  description?: string
  previewSize?: "sm" | "md" | "lg" // Preview image size
  showPreview?: boolean // Default: true

  // Callbacks
  onUploadSuccess?: (file: UploadedFileDto) => void
  onDeleteSuccess?: () => void
  onError?: (error: string) => void

  // Styling
  className?: string
  disabled?: boolean
}

const previewSizeClasses = {
  sm: "h-20 w-20",
  md: "h-32 w-32",
  lg: "h-48 w-48",
}

export default function FileUpload({
  value,
  onChange,
  allowedTypes,
  maxSize,
  label = "Upload File",
  description,
  previewSize = "md",
  showPreview = true,
  onUploadSuccess,
  onDeleteSuccess,
  onError,
  className,
  disabled = false,
}: FileUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)

  const {
    uploadedFile,
    previewUrl,
    uploading,
    deleting,
    error,
    handleFileSelect,
    handleDelete,
  } = useFileUpload({
    initialFileId: value,
    allowedTypes,
    maxSize,
    onUploadSuccess: (file) => {
      onChange?.(file.id)
      onUploadSuccess?.(file)
    },
    onDeleteSuccess: () => {
      onChange?.(null)
      onDeleteSuccess?.()
    },
    onError,
  })

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null
    handleFileSelect(file)
    // Reset input value after handling to allow selecting the same file again
    if (e.target) {
      e.target.value = ""
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!disabled) {
      setIsDragging(true)
    }
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    if (disabled) return

    const file = e.dataTransfer.files?.[0] || null
    if (file) {
      handleFileSelect(file)
    }
  }

  const handleClick = () => {
    if (!disabled) {
      fileInputRef.current?.click()
    }
  }

  const handleDeleteClick = async (e: React.MouseEvent) => {
    e.stopPropagation()
    await handleDelete()
    // Reset file input so user can upload the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  const isLoading = uploading || deleting
  const hasFile = !!uploadedFile || !!previewUrl

  return (
    <div className={cn("space-y-2", className)}>
      {label && <Label>{label}</Label>}
      {description && (
        <p className="text-sm text-muted-foreground">{description}</p>
      )}

      {/* File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={allowedTypes?.join(",")}
        onChange={handleFileInputChange}
        className="hidden"
        disabled={disabled}
      />

      {/* Upload Area */}
      {!hasFile && (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleClick}
          className={cn(
            "border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors",
            isDragging
              ? "border-primary bg-primary/5"
              : "border-input hover:border-primary/50",
            disabled && "opacity-50 cursor-not-allowed"
          )}
        >
          {isLoading ? (
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">
                {uploading ? "Uploading..." : "Deleting..."}
              </p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <Upload className="h-8 w-8 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">
                  Click to upload or drag and drop
                </p>
                {allowedTypes && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {allowedTypes
                      .map((type) => type.split("/")[1])
                      .join(", ")
                      .toUpperCase()}{" "}
                    up to {maxSize ? formatFileSize(maxSize) : "5MB"}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Preview Section */}
      {hasFile && showPreview && (
        <div className="space-y-2">
          <div className="flex items-start gap-4 p-4 border rounded-lg">
            {previewUrl && (
              <div className="relative">
                <img
                  src={previewUrl}
                  alt={uploadedFile?.name || "Preview"}
                  className={cn(
                    "object-cover rounded border",
                    previewSizeClasses[previewSize]
                  )}
                />
                {isLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-background/80 rounded">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  </div>
                )}
              </div>
            )}
            {!previewUrl && (
              <div
                className={cn(
                  "flex items-center justify-center bg-muted rounded border",
                  previewSizeClasses[previewSize]
                )}
              >
                <ImageIcon className="h-8 w-8 text-muted-foreground" />
              </div>
            )}

            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {uploadedFile?.name || "File"}
                  </p>
                  {uploadedFile?.size && (
                    <p className="text-xs text-muted-foreground">
                      {formatFileSize(uploadedFile.size)}
                    </p>
                  )}
                </div>
                {!disabled && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={handleDeleteClick}
                    disabled={isLoading}
                    className="h-8 w-8 shrink-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}
    </div>
  )
}

